<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row class="mb-2">
          <!-- Search -->
          <b-col cols="12" md="4" class="mb-2 mb-md-0">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block" placeholder="Search..." />
            </div>
          </b-col>

          <!-- Actions -->
          <b-col cols="12" md="8" class="d-flex align-items-center justify-content-md-end justify-content-start flex-wrap" style="gap: 10px;">
            <!-- Watch Now Button -->
            <b-button variant="outline-success" @click="openWatchVideo">
              <feather-icon icon="PlayIcon" size="16" class="mr-50" />
              Watch Now
            </b-button>

            <!-- Report Button -->
            <b-button variant="outline-info" @click="onClickMonthlyReport">
              <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
              Report
            </b-button>
            <b-button variant="outline-primary" @click="onClickImport">
              <feather-icon icon="UploadIcon" size="16" class="mr-50" />
              Import
            </b-button>

            <b-button
              variant="danger"
              @click="onClickBulkDelete"
              v-if="canDelete && selectedRows && selectedRows.length > 0"
              :disabled="bulkDeleteLoading || loadingUploadToFBR"
            >
              <b-spinner v-if="bulkDeleteLoading" small class="mr-50" />
              <feather-icon v-else icon="TrashIcon" size="16" class="mr-50" />
              Delete All
            </b-button>
            <b-button
              variant="secondary"
              @click="onClickBulkPrint"
              v-if="selectedRows && selectedRows.length > 0"
            >
              <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
              Print All
            </b-button>
            <b-button
              variant="success"
              @click="onClickUploadToFBR"
              v-if="selectedRows && selectedRows.length > 0"
              :disabled="loadingUploadToFBR"
            >
              <b-spinner v-if="loadingUploadToFBR" small class="mr-50" />
              <feather-icon v-else icon="UploadIcon" size="16" class="mr-50" />
              Upload to FBR
            </b-button>

            <b-button variant="primary" :to="{ name: 'sales-invoice-add' }">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Add Record
            </b-button>
          </b-col>
        </b-row>

        <!-- Filters -->
        <invoice-list-filters
          :customer-id-filter.sync="customerIDFilter"
          :sales-type-id-filter.sync="salesTypeIDFilter"
          :is-active-filter.sync="isActiveFilter"
          :fbr-status-filter.sync="fbrStatusFilter"
          :fbr-invoice-number-filter.sync="fbrInvoiceNumberFilter"
          :sale-date-from.sync="saleDateFrom"
          :sale-date-to.sync="saleDateTo"
        />
      </div>

      <!-- Select All Summary -->
      <b-alert v-if="selectAll && selectedSummary.total > 0" variant="primary" show class="mx-2 mb-2">
        <div class="alert-body font-weight-bold d-flex align-items-center justify-content-between">
          <span>
            <feather-icon icon="InfoIcon" class="mr-50" />
            You have selected all {{ selectedSummary.total }} matching invoices 
            (Pending: {{ selectedSummary.pendingCount }}, Invalid: {{ selectedSummary.invalidCount }}<template v-if="selectedSummary.postedCount > 0">, Posted: {{ selectedSummary.postedCount }}</template>).
          </span>
          <b-button size="sm" variant="outline-primary" @click="selectAll = false; toggleSelectAll()">
            Clear Selection
          </b-button>
        </div>
      </b-alert>

      <b-table
        ref="refInvoiceListTable"
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
        :busy="loading"
        sticky-header="true"
        :selectable="true"
        @row-selected="onRowSelected"
        select-mode="multi"
      >
        <template #table-busy>
          <table-skeleton :columns="tableColumns" :rows="5" />
        </template>

        <template #head(invoiceStatus)>
          <feather-icon icon="TrendingUpIcon" class="mx-auto" />
        </template>

        <!-- Custom Header Checkbox for select all -->
        <template #head(select)>
          <b-form-checkbox
            v-model="selectAll"
            class="custom-control-primary"
            @change="toggleSelectAll"
          />
        </template>

        <!-- Column: select -->
        <template #cell(select)="data">
          <!-- Show checkbox for ALL rows so they can be selected for Bulk Print -->
          <b-form-checkbox
            :checked="data.rowSelected"
            class="custom-control-primary"
            @change="data.toggleDetails(); $refs.refInvoiceListTable.selectRow(data.index)"
            @input="(val) => { if(val) $refs.refInvoiceListTable.selectRow(data.index); else $refs.refInvoiceListTable.unselectRow(data.index); }"
          />
        </template>

        <!-- Column: Id -->
        <template #cell(id)="data">
          {{ data.value }}
        </template>

        <!-- Column: docNo -->
        <template #cell(docNo)="data">
          <b-link
            :to="{ name: 'sales-invoice-view', params: { id: data.item.id } }"
            class="font-weight-bold"
          >
            <strong class="font-weight-bold"> {{ data.value }} </strong>
          </b-link>
        </template>

        <!-- Column: customer -->
        <template #cell(customer)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.avatar"
                :text="avatarText(data.item.metaCustomerName)"
                to="#"
              />
            </template>
            <b-link
              :to="{ name: 'customer-view', params: { id: data.item.customerID } }"
              class="font-weight-bold d-block text-nowrap text-capitalize"
            >
              {{ data.item.metaCustomerName }}
            </b-link>
            <div>
              <small class="text-muted"> ID: {{ data.item.customerID }} </small>
            </div>
          </b-media>
        </template>

        <!-- Column: Customer NTN -->
        <template #cell(customerNtn)="data">
          {{ getCustomerNtn(data.item) }}
        </template>

        <!-- Column: Customer CNIC -->
        <template #cell(customerCnic)="data">
          {{ getCustomerCnic(data.item) }}
        </template>

        <!-- Column: salesTypeID -->
        <template #cell(salesType)="data">
          <div class="font-weight-bold">
            {{ data.item.metaSalesTypeName }}
          </div>
          <small class="text-muted"> ID: {{ data.item.salesTypeID }} </small>
        </template>

        <!-- Column: fbrStatus -->
        <template #cell(fbrStatus)="data">
          <fbr-status-cell :key="`fbr-${data.item.id}-${data.item.fbrStatus}`" :data="data.item" />
        </template>

        <!-- Column: transDate -->
        <template #cell(transDate)="data">
          <span class="text-nowrap">
            {{ data.value }}
          </span>
        </template>

        <!-- Column: valWithoutTax -->
        <template #cell(valWithoutTax)="data">
          <div>
            {{ data.value }}
          </div>
        </template>

        <!-- Column: netAmount -->
        <template #cell(netAmount)="data">
          <div>
            {{ data.value }}
          </div>
        </template>

        <!-- Column: Created By -->
        <template #cell(addedOn)="data">
          <timestamp-cell :timestamp="data.item.addedOn" :byUser="data.item.metaAddedBy" />
        </template>

        <!-- Column: Updated By -->
        <template #cell(updatedOn)="data">
          <timestamp-cell :timestamp="data.item.updatedOn" :byUser="data.item.metaUpdatedBy" />
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              title="View Invoice"
              @click="$router.push({ name: 'sales-invoice-view', params: { id: data.item.id } })"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>

            <!-- Edit Action -->
            <b-button
              variant="flat-info"
              class="btn-icon rounded-circle"
              title="Edit Invoice"
              @click="$router.push({ name: 'sales-invoice-edit', params: { id: data.item.id } })"
              v-show="canModifyInvoice(data.item)"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>

            <!-- Print Action -->
            <b-button
              variant="flat-secondary"
              class="btn-icon rounded-circle"
              title="Print Invoice"
              @click="onClickPrint(data.item.id)"
              v-show="showPrintAction"
            >
              <feather-icon icon="PrinterIcon" size="16" />
            </b-button>

            <!-- Delete Action -->
            <b-button
              v-if="canDelete"
              variant="flat-danger"
              class="btn-icon rounded-circle"
              title="Delete Invoice"
              @click="onClickDelete(data.item)"
              :disabled="deleteLoading"
              :class="{ 'disabled-action': deleteLoading }"
              v-show="canModifyInvoice(data.item)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <label>Entries</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
            <span class="text-muted" v-if="dataMeta">
              Showing {{ dataMeta.from || 0 }} to {{ dataMeta.to || 0 }} of {{ dataMeta.of || 0 }} entries
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalInvoices"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

      <!-- Delete Confirmation Modal -->
      <delete-confirmation-modal
        :show.sync="isDeleteModalVisible"
        resource-name="Invoice"
        :item-name="selectedInvoice ? selectedInvoice.docNo : ''"
        :loading="deleteLoading"
        @deleteConfirmed="deleteSelectedInvoice"
        @hideModal="isDeleteModalVisible = false"
      />

      <!-- Bulk Delete Confirmation Modal -->
      <delete-confirmation-modal
        :show.sync="isBulkDeleteModalVisible"
        resource-name="Selected Invoices"
        :item-name="`${selectedRows.length} items`"
        :loading="bulkDeleteLoading"
        @deleteConfirmed="bulkDeleteInvoices"
        @hideModal="isBulkDeleteModalVisible = false"
      />

      <!-- Import Modal -->
      <invoice-list-import-modal
        :show.sync="isImportModalVisible"
        :loading="importLoading"
        @bulkImport="bulkImport"
      />

      <!-- FBR Upload Result Modal -->
      <b-modal
        id="modal-fbr-upload-result"
        v-model="isFBRResultModalVisible"
        :title="loadingUploadToFBR ? 'Uploading to FBR' : 'FBR Upload Results'"
        size="lg"
        hide-footer
        :no-close-on-backdrop="loadingUploadToFBR"
        :no-close-on-esc="loadingUploadToFBR"
        :hide-header-close="loadingUploadToFBR"
        scrollable
        header-bg-variant="light"
        header-text-variant="dark"
        modal-class="premium-modal"
        @hidden="refetchData"
      >
        <!-- Processing State -->
        <div v-if="loadingUploadToFBR" class="processing-container text-center py-5">
          <div class="spinner-wrapper mb-3">
            <div class="pulse-ring"></div>
            <b-spinner variant="primary" style="width: 5rem; height: 5rem;" class="main-spinner"></b-spinner>
          </div>
          <h2 class="mt-4 font-weight-bolder text-primary">Uploading...</h2>
          <p class="text-muted lead">Currently processing <strong>{{ selectedRows.length }}</strong> invoice(s)</p>
          <div class="progress-bar-minimal mt-4">
            <div class="progress-fill"></div>
          </div>
          
          <small class="text-secondary d-block mt-3">Please do not close this window or refresh the page.</small>
        </div>

        <!-- Results State -->
        <div v-else-if="fbrUploadResults" class="upload-results-container pb-2">
          <!-- Report Header -->
          <div class="report-header mb-1 p-1 bg-light rounded border-bottom d-flex justify-content-between align-items-center">
            <h6 class="font-weight-bolder text-primary mb-0">Batch Upload Report</h6>
            <div class="d-inline-flex align-items-center">
                          <small class="text-muted" style="font-size: 0.65rem;">{{ formatServerTime(fbrUploadResults.serverTime) }}</small>
            </div>
          </div>

          <!-- Results Summary Bar -->
          <div class="results-summary-bar d-flex align-items-center justify-content-between bg-white rounded p-25 mb-1 shadow-sm border">
            <div class="summary-item px-1 border-right flex-fill text-center">
              <div class="text-muted small font-weight-bold text-uppercase" style="font-size: 0.6rem;">Total</div>
              <div class="h6 mb-0 font-weight-bolder text-dark">{{ fbrUploadResults.data ? fbrUploadResults.data.totalCount : 0 }}</div>
            </div>
            <div class="summary-item px-1 border-right flex-fill text-center">
              <div class="text-success small font-weight-bold text-uppercase" style="font-size: 0.6rem;">Success</div>
              <div class="h6 mb-0 font-weight-bolder text-success">{{ fbrUploadResults.data ? fbrUploadResults.data.successCount : 0 }}</div>
            </div>
            <div class="summary-item px-1 flex-fill text-center">
              <div class="text-danger small font-weight-bold text-uppercase" style="font-size: 0.6rem;">Failed</div>
              <div class="h6 mb-0 font-weight-bolder text-danger">{{ fbrUploadResults.data ? fbrUploadResults.data.errorCount : 0 }}</div>
            </div>
          </div>

          <!-- Message Alert -->
          <b-alert
            v-if="fbrUploadResults.message"
            show
            :variant="fbrUploadResults.data && fbrUploadResults.data.successCount === fbrUploadResults.data.totalCount ? 'success' : 'light-warning'"
            fade
            class="mb-1 shadow-sm border-left-warning"
            style="border-left: 5px solid !important;"
          >
            <div class="d-flex align-items-center py-25">
              <feather-icon 
                :icon="fbrUploadResults.data && fbrUploadResults.data.successCount === fbrUploadResults.data.totalCount ? 'CheckCircleIcon' : 'AlertCircleIcon'" 
                :class="fbrUploadResults.data && fbrUploadResults.data.successCount === fbrUploadResults.data.totalCount ? 'text-success mr-50' : 'text-warning mr-50'"
                size="18"
              />
              <div class="small text-dark font-weight-bold">{{ fbrUploadResults.message }}</div>
            </div>
          </b-alert>

          <!-- Details Table Header -->
          <div class="d-flex justify-content-between align-items-center mb-1 px-1 mt-1">
            <h6 class="font-weight-bolder mb-0 text-dark">
              <feather-icon icon="ListIcon" size="14" class="mr-50" />
              Detailed Logs
            </h6>
            <b-badge pill variant="light-secondary" style="font-size: 0.7rem;">{{ fbrUploadResults.data ? fbrUploadResults.data.uploadResults.length : 0 }} items</b-badge>
          </div>

          <!-- Results Table -->
          <div class="table-responsive results-table-wrapper shadow-sm rounded border">
            <b-table
              :items="fbrUploadResults.data ? fbrUploadResults.data.uploadResults : []"
              :fields="fbrResultFields"
              hover
              responsive
              show-empty
              empty-text="No results found"
              class="mb-0 overflow-hidden"
              thead-class="bg-light"
            >
              <template #cell(invoiceId)="data">
                <span class="font-weight-bolder text-primary">#{{ data.value }}</span>
              </template>

              <template #cell(docNo)="data">
                <span class="text-dark font-weight-bold">{{ data.value || 'N/A' }}</span>
              </template>

              <template #cell(status)="data">
                <b-badge pill :variant="data.value === 'success' ? 'light-success' : 'light-danger'" class="text-capitalize px-1 py-25">
                  <feather-icon 
                    :icon="data.value === 'success' ? 'CheckCircleIcon' : 'XIcon'" 
                    size="12" 
                    class="mr-25" 
                  />
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(message)="data">
                <div class="error-message-cell d-flex align-items-start py-50" :title="data.value">
                  <span :class="data.item.status === 'error' ? 'text-danger font-weight-bold' : 'text-success'">
                    {{ formatFBRErrorMessage(data.value) }}
                  </span>
                </div>
              </template>
            </b-table>
          </div>

          <!-- Action Footer -->
          <div class="mt-2 d-flex justify-content-end align-items-center">
            <span v-if="fbrUploadResults.data && fbrUploadResults.data.errorCount > 0" class="text-danger small mr-1 font-weight-bold" style="font-size: 0.7rem;">
              <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
              {{ fbrUploadResults.data.errorCount }} error(s)
            </span>
            <b-button variant="primary" size="sm" @click="isFBRResultModalVisible = false" class="px-3 shadow-sm font-weight-bolder">
              Close Result
            </b-button>
          </div>
        </div>
      </b-modal>

      <!-- Monthly Report Config Modal -->
      <b-modal
        id="modal-monthly-report"
        v-model="isReportModalVisible"
        title="Monthly Summary Report"
        ok-title="Generate Report"
        cancel-variant="outline-secondary"
        @ok.prevent="generateMonthlyReport"
        centered
      >
        <b-row>
          <b-col cols="12" class="mb-2">
            <label>Select Month</label>
            <v-select
              v-model="selectedMonth"
              :options="monthOptions"
              placeholder="Select Month..."
              :reduce="option => option.value"
            />
          </b-col>
          <b-col cols="12">
            <label>Select Year</label>
            <v-select
              v-model="selectedYear"
              :options="yearOptions"
              placeholder="Select Year..."
              :reduce="option => option.value"
            />
          </b-col>
        </b-row>
      </b-modal>

    </b-card>
  </div>
</template>

<script>
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue';
import store from '@/store';
import { onMounted, onUnmounted, ref, watch, computed } from '@vue/composition-api';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BTooltip,
  BSpinner,
  BFormCheckbox,
  BAlert,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import useInvoicesList from './useInvoiceList';
import { avatarText } from '@core/utils/filter';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import invoiceStoreModule from '../invoiceStoreModule';
import { useToast } from 'vue-toastification/composition';
import InvoiceListFilters from './InvoiceListFilters.vue';
import InvoiceListImportModal from './InvoiceListImportModal.vue';
import FBRStatusCell from '@/views/apps/fbr-invoice/components/invoice-list/FBRStatusCell.vue';
import router from '@/router';

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    ToastificationContent,
    DeleteConfirmationModal,
    BFormCheckbox,
    BAlert,
    'b-tooltip': BTooltip,
    InvoiceListFilters,
    vSelect,
    InvoiceListImportModal,
    'fbr-status-cell': FBRStatusCell,
  },
  setup() {
    const toast = useToast();

    const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME);
    });

    const {
      loading,
      items,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,
      selectedRows,
      selectAll,
      selectedSummary,

      isActiveFilter,
      fbrStatusFilter,
      customerIDFilter,
      salesTypeIDFilter,
      fbrInvoiceNumberFilter,
      saleDateFrom,
      saleDateTo,

      fetchInvoices,
      refetchData,
      toggleSelectAll,
    } = useInvoicesList();

    onMounted(() => {
      fetchInvoices();
    });

    const loadingUploadToFBR = ref(false);

    const isDeleteModalVisible = ref(false);
    const deleteLoading = ref(false);

    const isBulkDeleteModalVisible = ref(false);
    const bulkDeleteLoading = ref(false);

    const monthOptions = [
      { label: 'January', value: 'Jan' },
      { label: 'February', value: 'Feb' },
      { label: 'March', value: 'Mar' },
      { label: 'April', value: 'Apr' },
      { label: 'May', value: 'May' },
      { label: 'June', value: 'Jun' },
      { label: 'July', value: 'Jul' },
      { label: 'August', value: 'Aug' },
      { label: 'September', value: 'Sep' },
      { label: 'October', value: 'Oct' },
      { label: 'November', value: 'Nov' },
      { label: 'December', value: 'Dec' },
    ];

    const currentYear = new Date().getFullYear();
    const currentMonthIndex = new Date().getMonth();

    const selectedMonth = ref(monthOptions[currentMonthIndex].value);
    const selectedYear = ref(String(currentYear));

    const yearOptions = Array.from({ length: 5 }, (_, i) => ({
      label: String(currentYear + 1 - i),
      value: String(currentYear + 1 - i),
    }));

    const isReportModalVisible = ref(false);

    const onClickMonthlyReport = () => {
      isReportModalVisible.value = true;
    };

    const generateMonthlyReport = () => {
      if (!selectedMonth.value || !selectedYear.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Please select both Month and Year for the report',
          },
        });
        return;
      }
      
      const routeData = router.resolve({
        name: 'fbr-monthly-summary-report',
        query: {
          month: selectedMonth.value,
          year: selectedYear.value
        }
      });
      window.open(routeData.href, '_blank');
      isReportModalVisible.value = false;
    };

    // Dynamic Permission Logic matching ActionCell.vue
    const canDelete = computed(() => {
      const authRoutes = store.state.user?.authRoutes || {};
      const permissions = authRoutes['sales-invoice'];
      if (permissions && permissions.delete !== undefined) {
        return permissions.delete === true;
      }
      return true; // Default fallback if no permissions explicitly restricted
    });

    const isImportModalVisible = ref(false);
    const importLoading = ref(false);

    const selectedInvoice = ref(null);

    const isFBRResultModalVisible = ref(false);
    const fbrUploadResults = ref(null);

    const fbrResultFields = [
      {
        key: 'invoiceId',
        label: 'ID',
        sortable: true,
        thClass: 'text-center',
        tdClass: 'text-center',
      },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'message', label: 'Response Message' },
    ];

    const formatServerTime = (time) => {
      if (!time) return 'N/A';
      try {
        const date = new Date(time);
        return date.toLocaleString('en-US', {
          dateStyle: 'medium',
          timeStyle: 'medium',
        });
      } catch (e) {
        return time;
      }
    };

    const calculateRate = (res) => {
      if (!res || !res.data || !res.data.totalCount) return 0;
      return Math.round((res.data.successCount / res.data.totalCount) * 100);
    };

    const formatFBRErrorMessage = (msg) => {
      if (!msg) return 'N/A';
      
      // Bro, let's extract the actual error from that long technical string
      if (msg.includes('Unauthorized - {')) {
        try {
          const jsonPart = msg.substring(msg.indexOf('{'));
          const errorObj = JSON.parse(jsonPart);
          
          // Try to find the most useful message
          if (errorObj?.details?.fault?.message) {
            return `Unauthorized: ${errorObj.details.fault.message}`;
          }
          if (errorObj?.details?.fault?.description) {
            return `Error: ${errorObj.details.fault.description}`;
          }
          return 'Unauthorized: Invalid Credentials';
        } catch (e) {
          return 'Unauthorized: Access Failure (401)';
        }
      }

      // If it's a generic FBR Validation API error, simplify it
      if (msg.includes('FBR API returned error:')) {
         return msg.split('FBR API returned error:').pop().trim();
      }

      return msg;
    };

    // Print action visibility control - default false
    const showPrintAction = ref(true);

    const onClickDelete = (invoice) => {
      selectedInvoice.value = invoice;
      isDeleteModalVisible.value = true;
    };

    const deleteSelectedInvoice = () => {
      deleteLoading.value = true;

      store
        .dispatch('sales-invoice/deleteInvoice', selectedInvoice.value.id)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Invoice Deleted Successfully',
            },
          });
        })
        .catch((error) => {
          console.error('Error deleting invoice:', error);
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.message,
            },
          });
        })
        .finally(() => {
          deleteLoading.value = false;
          isDeleteModalVisible.value = false;
          refetchData();
        });
    };

    const onClickBulkDelete = () => {
      const hasPosted = selectedRows.value.some(row => !['pending', 'Invalid', null].includes(row.fbrStatus));
      
      if (hasPosted) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Delete Blocked',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Selection contains posted invoices which cannot be deleted. Please unselect them to delete the other pending or invalid invoices.',
          },
        });
        return;
      }

      isBulkDeleteModalVisible.value = true;
    };

    const bulkDeleteInvoices = () => {
      bulkDeleteLoading.value = true;
      const validRows = selectedRows.value.filter(row => ['pending', 'Invalid', null].includes(row.fbrStatus));
      if (validRows.length === 0) {
        bulkDeleteLoading.value = false;
        return;
      }
      const ids = validRows.map((row) => row.id);

      store
        .dispatch('sales-invoice/BulkDelete', ids)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Invoices Deleted Successfully',
            },
          });
          
          if (selectAll.value) {
            selectAll.value = false;
          }
          selectedRows.value = [];
          if (refInvoiceListTable.value) {
            refInvoiceListTable.value.clearSelected();
          }
        })
        .catch((error) => {
          console.error('Error bulk deleting invoices:', error);
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.message || error || 'Failed to delete invoices',
            },
          });
        })
        .finally(() => {
          bulkDeleteLoading.value = false;
          isBulkDeleteModalVisible.value = false;
          refetchData();
        });
    };

    const onClickImport = () => {
      isImportModalVisible.value = true;
    };

    const bulkImport = async (data, onComplete) => {
      importLoading.value = true;

      try {
        const response = await store.dispatch('sales-invoice/bulkUploadInvoice', data);

        const responseData = response?.data || {};
        const innerData = responseData?.data || {};

        const hasValidationErrors = innerData?.validationErrors && innerData.validationErrors.length > 0;
        const errorCount = innerData?.errorCount || 0;

        if (!hasValidationErrors && errorCount === 0 && responseData.status !== 'error') {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: `Successfully imported ${data.length} invoices`,
            },
          });
        }

        // Refresh the data
        refetchData();

        // Call the completion callback with success AND the response so it shows errors
        if (onComplete) {
          onComplete(true, response);
        }
      } catch (error) {
        console.error('Bulk import error:', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.message || 'Failed to import invoices',
          },
        });

        // Call the completion callback with error
        if (onComplete) {
          onComplete(false, error);
        }
      } finally {
        importLoading.value = false;
      }
    };

    const onClickUploadToFBR = () => {
      const hasPosted = selectedRows.value.some((row) => isInvoicePosted(row));

      if (hasPosted) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Upload Blocked',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Selection contains already posted invoices. Please unselect them to upload your other pending invoices.',
          },
        });
        return;
      }

      const validRows = selectedRows.value.filter((row) => canUploadInvoiceToFBR(row));

      if (validRows.length === 0) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Upload Notice',
            icon: 'InfoIcon',
            variant: 'info',
            text: 'No pending or invalid invoices selected for FBR upload.',
          },
        });
        return;
      }

      loadingUploadToFBR.value = true;
      fbrUploadResults.value = null; // Clear previous results
      isFBRResultModalVisible.value = true; // Show modal immediately in loading state
      
      const ids = validRows.map((row) => row.id);
      
      // Force reactivity by creating new object references
      items.value = items.value.map((row) => {
        if (ids.includes(row.id)) {
          return {
            ...row,
            fbrStatus: 'processing',
          };
        }
        return { ...row };
      });

      store
        .dispatch('sales-invoice/UploadToFBR', ids)
        .then((response) => {
          console.log('RESPONSE:', response);

          // results is the root response object {status, message, data: {uploadResults...}}
          const results = response;
          fbrUploadResults.value = results;
          isFBRResultModalVisible.value = true;

          // If there's a status, update rows based on individual results if available
          if (results.data && results.data.uploadResults && Array.isArray(results.data.uploadResults)) {
            items.value = items.value.map((row) => {
              const res = results.data.uploadResults.find((r) => r.invoiceId === row.id);
              if (res) {
                return {
                  ...row,
                  fbrStatus: res.status === 'success' ? 'uploaded' : 'Invalid',
                  fbrNote: res.message,
                };
              }
              return { ...row };
            });
          } else {
            // Fallback for simple success
            items.value = items.value.map((row) => {
              if (ids.includes(row.id)) {
                return {
                  ...row,
                  fbrStatus: results.status === 'success' ? 'uploaded' : 'Invalid',
                };
              }
              return { ...row };
            });
          }

          if (results.status === 'success' && results.data && results.data.errorCount === 0) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'CheckCircleIcon',
                variant: 'success',
                text: results.message || 'All invoices uploaded successfully',
              },
            });
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: results.status === 'error' ? 'Upload Errors' : 'Partial Success',
                icon: 'AlertTriangleIcon',
                variant: (results.data && results.data.successCount > 0) ? 'warning' : 'danger',
                text: results.message || 'Check the details in the results modal',
              },
            });
          }
        })
        .catch((error) => {
          console.error('Bulk upload error:', error);
          toast({
            component: ToastificationContent,
            props: {
              title: 'Upload Failed',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.message || 'Failed to connect to the server',
            },
          });

          items.value = items.value.map((row) => {
            if (ids.includes(row.id)) {
              return {
                ...row,
                fbrStatus: 'Invalid',
              };
            }
            return { ...row };
          });
        })
        .finally(() => {
          loadingUploadToFBR.value = false;
        });
    };

    const onRowSelected = (rows) => {
      // If selectAll is active, we don't want local page navigation to override our global selection
      if (selectAll.value) return;

      selectedRows.value = rows;
    };

    // Video link for Watch Now button
    const invoiceVideoLink = 'https://cdn.cloudious.net/file-1756982991165-390869850.mp4';

    // Watch video function
    const openWatchVideo = () => {
      window.open(invoiceVideoLink, '_blank');
    };

    // Print function
    const onClickPrint = (invoiceId) => {
      const routeData = router.resolve({ name: 'sales-invoice-print', params: { id: invoiceId } });
      window.open(routeData.href, '_blank');
    };

    const onClickBulkPrint = () => {
      if (!selectedRows.value || selectedRows.value.length === 0) return;
      const ids = selectedRows.value.map((row) => row.id).join(',');
      const routeData = router.resolve({ name: 'sales-invoice-print', params: { id: ids } });
      window.open(routeData.href, '_blank');
    };

    const formatListFieldValue = (value) => {
      const trimmed = value !== null && value !== undefined ? String(value).trim() : '';
      return trimmed || '-';
    };

    const getFbrStatus = (item) => {
      return item && item.fbrStatus != null ? String(item.fbrStatus).toLowerCase() : '';
    };

    const getFbrInvoiceNo = (item) => {
      return item && item.fbrInvoiceNo ? String(item.fbrInvoiceNo).trim().toLowerCase() : '';
    };

    const isInvoicePosted = (item) => {
      if (!item) return false;

      const status = getFbrStatus(item);
      if (status === 'posted' || status === 'uploaded') {
        return true;
      }

      const fbrInvoiceNo = getFbrInvoiceNo(item);
      return !!fbrInvoiceNo && fbrInvoiceNo !== '-' && fbrInvoiceNo !== 'posted';
    };

    const canUploadInvoiceToFBR = (item) => {
      if (!item || isInvoicePosted(item)) return false;

      const status = getFbrStatus(item);
      return !status || status === 'pending' || status === 'invalid';
    };

    const canModifyInvoice = (item) => {
      if (!item) return false;

      const status = getFbrStatus(item);

      if (status === 'posted' || status === 'uploaded' || status === 'processing') {
        return false;
      }

      if (!status || status === 'pending' || status === 'invalid') {
        return true;
      }

      const fbrInvoiceNo = getFbrInvoiceNo(item);
      return !fbrInvoiceNo || fbrInvoiceNo === '-' || fbrInvoiceNo === 'posted';
    };

    const getCustomerNtn = (item) => {
      return formatListFieldValue(
        item.metaCustomerNtn
          || item.metaNtn
          || item.customerNtn
          || item.ntnNumber
          || item.ntn
      );
    };

    const getCustomerCnic = (item) => {
      return formatListFieldValue(
        item.metaCustomerCnic
          || item.metaCnic
          || item.customerCnic
          || item.cnicNumber
          || item.cnic
      );
    };

    // Function to enable print action
    const enablePrintAction = () => {
      showPrintAction.value = true;
    };

    // Function to disable print action
    const disablePrintAction = () => {
      showPrintAction.value = false;
    };

    return {
      loading,
      items,
      loadingUploadToFBR,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,
      selectedInvoice,
      selectedRows,
      selectAll,
      selectedSummary,

      isActiveFilter,
      fbrStatusFilter,
      customerIDFilter,
      salesTypeIDFilter,
      fbrInvoiceNumberFilter,
      saleDateFrom,
      saleDateTo,

      selectedMonth,
      selectedYear,
      monthOptions,
      yearOptions,
      isReportModalVisible,
      onClickMonthlyReport,
      generateMonthlyReport,

      isDeleteModalVisible,
      deleteLoading,

      isBulkDeleteModalVisible,
      bulkDeleteLoading,

      isFBRResultModalVisible,
      fbrUploadResults,
      fbrResultFields,
      formatServerTime,
      calculateRate,
      formatFBRErrorMessage,

      isImportModalVisible,
      importLoading,

      canDelete,

      onClickUploadToFBR,
      onClickBulkDelete,
      bulkDeleteInvoices,
      onRowSelected,
      onClickImport,
      bulkImport,
      onClickDelete,
      deleteSelectedInvoice,
      avatarText,
      refetchData,
      toggleSelectAll,
      openWatchVideo,
      onClickPrint,
      onClickBulkPrint,
      canModifyInvoice,
      getCustomerNtn,
      getCustomerCnic,
      showPrintAction,
      enablePrintAction,
      disablePrintAction,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.b-table-sticky-header {
  max-height: 80vh !important;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.premium-modal {
  ::v-deep .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  ::v-deep .modal-header {
    padding: 0.75rem 1.5rem; // Compact padding
    background-color: #f8f9fa !important; // Light grey background
    border-bottom: 1px solid #ebe9f1;
    .modal-title {
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      font-size: 0.9rem; // Slightly smaller
      color: #5e5873 !important; // Dark grey text
    }
    .close {
      color: #5e5873 !important;
      opacity: 1 !important;
      text-shadow: none;
      &:hover { opacity: 0.7 !important; }
    }
  }
}

.report-header {
  background-color: #f8f9fa !important;
  border-bottom: 2px dashed #ebe9f1 !important;
}

.report-details-box {
  background-color: #ffffff !important;
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 4px 15px rgba(0,0,0,0.05) !important;
  }
}

.processing-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  .spinner-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    .pulse-ring {
      content: '';
      width: 120px;
      height: 120px;
      background: rgba(115, 103, 240, 0.15);
      border-radius: 50%;
      position: absolute;
      animation: pulse 2s infinite ease-out;
    }
  }
}

@keyframes pulse {
  0% { transform: scale(0.7); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.progress-bar-minimal {
  width: 280px;
  height: 8px;
  background: #f3f2f7;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);

  .progress-fill {
    width: 80px;
    height: 100%;
    background: linear-gradient(90deg, #7367f0, #9e95f5);
    border-radius: 10px;
    position: absolute;
    animation: slide 1.8s infinite ease-in-out;
  }
}

@keyframes slide {
  0% { left: -100px; }
  100% { left: 300px; }
}

.results-summary-bar {
  background-color: #ffffff !important;
  .summary-item {
    transition: transform 0.2s ease;
    &:hover { transform: translateY(-2px); }
    &:last-child {
      border-right: none !important;
    }
  }
}

.results-table-wrapper {
  background: white;
  ::v-deep {
    .table {
      font-size: 0.85rem;
      th { 
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.5px;
        background-color: #f8f9fa;
        color: #5e5873;
      }
      td { vertical-align: middle !important; }
    }
  }
}

.border-left-primary { border-left: 4px solid #7367f0 !important; }
.border-left-warning { border-left: 4px solid #ff9f43 !important; }
.border-left-success { border-left: 4px solid #28c76f !important; }
.border-left-danger { border-left: 4px solid #ea5455 !important; }

.error-message-cell {
  max-width: 450px;
  span { line-height: 1.4; }
}

.b-table-sticky-header > .table.b-table > tbody > tr.b-table-row-selected > .b-table-sticky-column {
  background-color: var(--primary) !important;
  * {
    color: white !important;
  }
}
</style>
